import {
	CommercialCodeUnit,
	CommercialCodeSubUnit,
	CommercialCodeSubSubUnit
} from '../../shared/models/boc-client-transac';
import { CodeValue } from '../../shared/models/code-value';

export interface N4sShipmentOption {
	tmode: string;
	incoterm: string;
}

// Transport mode codes (SAP `tmode` values). Single source of truth for N4S.
const TRANSPORT_MODE_SEA = '1';
const TRANSPORT_MODE_RAIL = '2';
const TRANSPORT_MODE_TRUCK = '3';
const TRANSPORT_MODE_INLAND_WATERWAY = '5';

// Incoterm codes.
const INCOTERM_DAP = 'DAP';
export const INCOTERM_CIP = 'CIP';

// Defaults applied when no shipment-specific value is available.
export const DEFAULT_TRANSPORT_MODE = TRANSPORT_MODE_TRUCK;
export const DEFAULT_INCOTERM = INCOTERM_DAP;

const TMODE_TR_KEY: Record<string, string> = {
	[TRANSPORT_MODE_SEA]: 'tmode.bysea',
	[TRANSPORT_MODE_RAIL]: 'tmode.rail',
	[TRANSPORT_MODE_TRUCK]: 'tmode.lorry',
	[TRANSPORT_MODE_INLAND_WATERWAY]: 'tmode.inlandwaterway'
};

export function getTmodeTrKey(tmode: string): string {
	return TMODE_TR_KEY[tmode] ?? `tmode.${ tmode }`;
}

export const VARIABLE_SPECIFIC_RULE = new Set(['usepartreference']);
export const METAL_STUDS = 'METAL_STUDS';

export function isVariableSpecificField(field: string): boolean {
	return VARIABLE_SPECIFIC_RULE.has(field.toLowerCase());
}

export function getValidPayer(value: unknown): string | undefined {
	if (typeof value !== 'string' || value.length === 0) {
		return undefined;
	}
	return [...value].every(character => character >= '0' && character <= '9') ? value : undefined;
}

export const TOLERANCE_FIELD = 'Tolerance';

export const DATETIME_FORMAT_PATTERN = 'YYYY-MM-DD[T]HH:mm:ss.SSSS';
export const PRICE_DEFAULT = '─';

export interface Need4SteelOffer {
	header: Need4SteelOfferHeader;
}

export interface NumericRange {
	min: number;
	max: number;
	minExcluded: boolean;
	maxExcluded: boolean;
}
export interface BocOpt {
	soldTo?: string;
	shipTo?: string;
	payer?: string;
}

interface OfferHeaderId {
	id?: number;
	externalId?: string;
}

interface Need4SteelOfferHeader extends BocOpt, OfferHeaderId {
	coNbr?: string;
	soldTo?: string;
	shipTo?: string;
	payer?: string;
	deliveryWeek?: string;
	idd?: string;
	mill?: string;
	comments?: string;
	coilWeightType?: string;
}

export interface MillOfferNeed4Steel {
	id: string;
	deliveryWeek: number;
	deliveryYear: number;
	idd: string;
	iddDescription?: string;
	millSite?: string;
	millSiteCode?: string;
	millSiteDescription?: string;
	category: string;
	categoryDescription: string;
	subCategory: string;
	subCategoryDescription: string;
	form: string;
	formDescription: string;
	dplconstraintsList?: DPLScopeConstraints[];
	variablesList?: Variable[];
	customerTemplateReference?: string;
	templateNumber?: number;
	rfqNumber?: string;
	dealType: string;
	metalStud?: boolean;
}

export interface DPLScopeConstraints {
	qliId: string;
	sqn: string;
	dplScopeId?: string;
	settingGroups: DPLConstraintGroup[];
}

export interface DPLConstraintGroup {
	settings: DPLConstraint[];
}

interface DPLConstraint {
	fieldName: string;
	attributeValues: ListValue[];
	minValue: number;
	maxValue: number;
	minValueExcluded: boolean;
	maxValueExcluded: boolean;
}

export interface Variable {
	field: string;
	fieldDescription: string;
	templateProperty: TemplateProperty;
}

interface TemplateProperty {
	type: 'string' | 'integer' | 'number' | 'array';
	variable?: boolean;
	path: string;
	maxLength?: number;
	free?: boolean;
	properties?: Properties;
	maxItems?: number;
	maximum?: number;
}

interface Properties {
	finishingLine: FinishingLine;
}

interface FinishingLine {
	type?: string;
	path?: string;
	maxLength?: number;
	free?: boolean;
}

export interface ListValue {
	code: string;
	description: string;
}

export interface ValueRange {
	minValue: number;
	maxValue: number;
	minValueExcluded?: boolean;
	maxValueExcluded?: boolean;
}

type Constraint = Record<string, string[] | ValueRange>;

interface ConstraintGroup {
	constraints: Constraint[];
}

export interface DplScope {
	soldTo: string;
	productCategory: string;
	productSubCategory: string;
	dealType: string;
	constraintGroups: ConstraintGroup[];
}

export interface State {
	millOfferId?: string;
}

export interface MillOrder {
	offerRfq: string;
	deliveryWeek: number;
	deliveryYear: number;
	soldTo: string;
	shipTo: string;
	endUser: string;
	isPriceRequest: boolean;
	idd: string;
	millSite: string;
	transportMode: string;
	incoterms?: string;
	products?: Product[];
	dealType: string;
	codeUnitRef?: string;
	codeSubUnitRef?: string;
	codeSubSubUnitRef?: string;
}

interface Product {
	qliId: string;
	sqn: string;
	coils: number;
	itemNumber: string;
	need4steelProductDataList: N4sEventsProductData[];
	productCharacteristics: Record <string, string>;
}

export interface N4sEventsProductData {
	id?: number;
	usageEventId?: number;
	characName: string;
	characValueText?: string;
	characValueTable?: string;
}

export interface MillOrderPriceResponse {
	priceList?: OrderPrice[];
	isError?: boolean;
	message?: string;
	effectivePrice?: number;
}

export interface ExchangeRateValue {
	currency: string;
	value: number;
}

export interface ExchangeRateLastResponse {
	referenceDate?: string;
	rates: ExchangeRateValue[];
}

interface OrderPrice {
	qliId: string;
	effectivePrice: number;
	message: string;
	ok: boolean;
}

export interface BocClient {
	businessId: string;
	name: string;
	city: string;
	countryCode: string;
	referenceCommercialUnit?: CommercialCodeUnit;
	referenceCommercialSubUnit?: CommercialCodeSubUnit;
	referenceCommercialSubSubUnit?: CommercialCodeSubSubUnit;
}

export interface RequestOrder {
	id?: number;
	creatorUserRef?: number;
	organizationRef?: number;
	transportModeTr: number;
	ebusinessRef?: number;
	soldToRef: string;
	shipToRef: string;
	endUserRef?: string;
	rfqNumber: string;
	offerRef: string;
	purchaseOrderNumber?: string;
	deliveryDateTypeTr?: string;
	deliveryDateDisplay?: string;
	iddTr: string;
	orderStatusTr?: string;
	comments?: string;
	deliveryDate: Date | string;
	creationDate?: Date;
	creationBy?: string;
	modificationDate?: Date;
	modificationBy?: string;
	coilWeightType?: string;
	dealType?: string;
	codeUnitRef?: string;
	codeSubUnitRef?: string;
	codeSubSubUnitRef?: string;
	exchangeRate?: number;
	exchangeRateReferenceDate?: string;
	listOfRequestOrderItem: RequestOrderItem[];
	payer?: string;
	millSite?: string;
	totalAmount?: number;
}
export interface RequestOrderItem {
	id?: number;
	requestOrderId?: number;
	itemNumber?: string;
	productRef: string;
	qliId: string;
	connectionKey?: string;
	configurationRef?: string;
	templateRef: string;
	productStatusTr?: string;
	soldToRef: string;
	shipToRef: string;
	transportModeTr: string;
	deliveryDateTypeTr?: string;
	deliveryDate: Date | string;
	deliveryDateDisplay?: string;
	incotermTr?: string;
	incoterm2Tr?: string;
	quantityAmount: number;
	quantityUnitTr: string;
	priceAmount?: number;
	priceCurrencyTr: string;
	priceWeightTr: string;
	creationDate?: Date;
	creationBy?: string;
	modificationDate?: Date;
	modificationBy?: string;
	endUserRef?: string;
	totalAmount?: number;
	totalUnitTr?: string;
	listOfRequestProductData: RequestProductData[];
	listOfRequestServiceLevel: RequestServiceLevel[];
	sqnNumber: string;
}

export interface RequestProductData {
	id?: number;
	requestOrderItemId?: number;
	characName: string;
	type: string;
	characValueText?: string;
	characValueUnitTr?: string;
	characValueTable?: string;
	characValueNum?: number;
	creationDate?: Date;
	creationBy?: string;
	modificationDate?: Date;
	modificationBy?: string;
}

interface RequestServiceLevel {
	id?: number;
	requestOrderItemId?: number;
	serviceLevelRef?: string;
	creationDate?: Date;
	creationBy?: string;
}

export interface WeightType {
	code: string;
	value: string;
}

export interface ProductDetails {
	label: string;
	value: string | number | undefined;
	isNumeric: boolean;
	fontType: string;
}
export interface OfferRequest {
	soldTo: string;
	productCategory?: string;
	productSubCategory?: string;
	dealType?: string;
}

export interface OrderHistory {
	[key: string]: string | number | CodeValue;
	lastUsed: string;
	steelGrade: CodeValue;
	thicknessValue: number;
	widthValue: number;
	maxNetWeightValue: number;
	thicknessTolerance: CodeValue;
	coating: CodeValue;
	additionalFields: string;
}

export const IGNORED_VARIABLES = new Set([
	'steelGrade',
	'lastUsed',
	'thicknessValue',
	'widthValue',
	'thicknessTolerance',
	'coating',
	'category',
	'subCategory',
	'form',
	'finishingLine',
	'maxNetWeightValue',
	'maxWeightPerWidthUnitValue'
]);

export interface LogisticsConstraints {
	id: number;
	millCode: string;
	countryCode: string;
	category?: string;
	subCategory?: string;
	form?: string;
	transportMode?: number;
	maxCoilWeight?: number;
	packagingCode?: string;
}
